import { ReactElement, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { useTranslation } from 'next-i18next';
import { IconButton, Spinner, Tooltip } from 'components';
import ButtonV3 from 'components/ComponentV2/ButtonV3';
import { Row } from 'components/layout';

import EditCompanyProfileInfo from '../EditCompanyProfileInfo';
import SaveChangesModal from '../SaveChangesModal';
import { useCompanyProfile } from '../../../hooks/UseCompanyProfile.hook';

import styles from './EditCompanyProfile.module.scss';

export const EditCompanyProfile = (): ReactElement => {
  const companyData = useCompanyProfile();
  const formRef = useRef<HTMLFormElement>();
  const [t] = useTranslation('common');

  const router = useRouter();
  const { onboarding } = router.query;

  const [isOpenSaveModal, setIsOpenSaveModal] = useState(false);
  const [isShowSaveModal, setIshowSaveModal] = useState(false);

  if (!companyData?.company) return <Spinner size="large" wrapper />;

  const onCancel = () => {
    setIsOpenSaveModal(false);
    history.back();
  };

  return (
    <>
      <Row className={styles.Header} justify="space-between">
        <Row align="center">
          <IconButton
            size="small"
            iconName="close"
            className={styles.cancelIcon}
            onClick={() => {
              if (isShowSaveModal) {
                setIsOpenSaveModal(true);
              } else {
                history.back();
              }
            }}
          />
          <p className={styles.HeaderTitle}>
            {onboarding === 'true' ? t('company-profile') : t('edit-profile')}
          </p>
        </Row>
        <Row className={styles.buttonsRow}>
          <Tooltip
            content={t('view-profile-tooltip')}
            position="bottom"
            color="dark"
            className={styles.tooltip}
            hasWhitespaceNoWrap={false}
          >
            <ButtonV3
              variant="contained"
              color="secondary"
              type="button"
              className={styles.buttons}
              size="small"
              borderRadius={8}
            >
              <a
                href={`/companies/${companyData.company.slug}`}
                target="_blank"
                rel="noreferrer"
              >
                {t('view-profile')}
              </a>
            </ButtonV3>
          </Tooltip>

          <ButtonV3
            type="submit"
            className={styles.buttons}
            form="company-form"
            size="small"
            borderRadius={8}
            isLoading={companyData.isLoading}
          >
            {t('save-changes')}
          </ButtonV3>
        </Row>
      </Row>
      <div></div>

      <div className={styles.content}>
        <section className={styles.CompanyProfile} data-testid="CompanyProfile">
          <EditCompanyProfileInfo
            reference={formRef}
            setIshowSaveModal={setIshowSaveModal}
            onboarding={onboarding === 'true'}
            {...companyData}
          />
        </section>
      </div>
      {isOpenSaveModal ? (
        <SaveChangesModal
          isOpen={isOpenSaveModal}
          onClose={onCancel}
          onSave={() => {
            formRef?.current?.submitForm();
            onCancel();
          }}
          setOpen={setIsOpenSaveModal}
        />
      ) : null}
    </>
  );
};
